/**
 * goldPriceService.js
 * Fetches live gold prices from the backend CRM API.
 * Returns rates array with karat mappings for all purities (999, 995, 916, 750, 585).
 */

import API from './api';

// Simple in-memory cache so we don't hammer the endpoint on every render
let _cache = null;        // { rates: [...], date, fetchedAt }
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all gold rates per gram (₹).
 * Returns full rates array with karat, purity, perGram, per10gram for each purity.
 *
 * @returns {Promise<{ rates: Array<{karat, purity, perGram, per10gram}>, date: string } | null>}
 */
export async function fetchHallmarkGoldRate() {
  // Return cached value if still fresh
  if (_cache && Date.now() - _cache.fetchedAt < CACHE_TTL_MS) {
    return { rates: _cache.rates, date: _cache.date };
  }

  try {
    const response = await API.get('/gold-rates');
    console.log('[goldPriceService] Fetched gold rates:', response.data);
    
    if (!response.data?.rates || !Array.isArray(response.data.rates)) {
      throw new Error('Unexpected response shape - missing rates array');
    }

    // Pass through the rates array directly with karat mappings
    _cache = { rates: response.data.rates, date: response.data.date, fetchedAt: Date.now() };

    return { rates: response.data.rates, date: response.data.date };
  } catch (err) {
    console.warn('[goldPriceService] Failed to fetch gold price:', err.message);
    return null;
  }
}

/** Invalidate the in-memory cache (e.g. on manual refresh). */
export function invalidateGoldPriceCache() {
  _cache = null;
}
